import { ReactNode } from "react";
import { Box, IconButton } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReplayIcon from "@mui/icons-material/Replay";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { EntityTemplateField } from "../../entityTemplates/EntityTemplate.types.ts";
import { ContractModel } from "../contract/Contract.types.ts";
import { ValidationError } from "@common/validationError.ts";
import ContractTemplateField from "../contractTemplateFields/ContractTemplateField.tsx";
import { FieldCitation } from "../addContractButton/createContractPane/CreateContractPane.types.ts";
import ContractAiUpdateFieldWrapper from "./ContractAiUpdateFieldWrapper.tsx";
import { useContractAiUpdateStyles } from "./ContractAiUpdate.styles.ts";

export interface ContractAiUpdateFieldRowProps {
    field: EntityTemplateField;
    currentContract: ContractModel;
    proposedContract: ContractModel;
    currentTagIds: Array<string>;
    proposedTagIds: Array<string>;
    citation: FieldCitation | undefined;
    /** True when the AI proposed something other than the contract's value, whichever side is showing now. */
    canToggle: boolean;
    isShowingCurrentValue: boolean;
    validationErrors: Array<ValidationError>;
    onProposedChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    onProposedTagsChange: (tagIds: Array<string>) => void;
    onToggle: () => void;
    onShowInDocument: (quote: string) => void;
    currentCounterPartiesSlot?: ReactNode;
    proposedCounterPartiesSlot?: ReactNode;
}

/**
 * One template field, current on the left and proposed on the right. No row label and no "was X, now Y":
 * both renderers carry the field's own label, and saying it a third time is what made the earlier design
 * unreadable.
 */
function ContractAiUpdateFieldRow({
    field,
    currentContract,
    proposedContract,
    currentTagIds,
    proposedTagIds,
    citation,
    canToggle,
    isShowingCurrentValue,
    validationErrors,
    onProposedChange,
    onProposedTagsChange,
    onToggle,
    onShowInDocument,
    currentCounterPartiesSlot,
    proposedCounterPartiesSlot,
}: ContractAiUpdateFieldRowProps) {
    const { translateString } = useTranslation();
    const styles = useContractAiUpdateStyles();

    return (
        <Box sx={styles.row}>
            {/* readOnly alone only swaps onChange for a no-op, so the reader could still click in and type
                into a field that silently discards it. disabled is what actually stops them. */}
            <Box sx={{ ...styles.column, ...styles.readOnlyColumn }}>
                <ContractAiUpdateFieldWrapper>
                    <ContractTemplateField
                        field={field}
                        contract={currentContract}
                        onChange={async () => {}}
                        validationErrors={[]}
                        selectedTagIds={currentTagIds}
                        onTagsChange={() => {}}
                        readOnly
                        disabled
                        hideToolTip
                        noMargin
                        hideAutoRenewal
                        counterPartiesSlot={currentCounterPartiesSlot}
                    />
                </ContractAiUpdateFieldWrapper>
            </Box>

            <Box sx={styles.arrowCell}>
                {canToggle && (
                    <DotLegalTooltip text={translateString(isShowingCurrentValue ? "useAiSuggestion" : "useCurrentValue")}>
                        <IconButton size="small" onClick={onToggle}>
                            {isShowingCurrentValue ? <ReplayIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                        </IconButton>
                    </DotLegalTooltip>
                )}
            </Box>

            <Box sx={styles.column}>
                <ContractAiUpdateFieldWrapper citation={citation} onShowInDocument={onShowInDocument}>
                    <ContractTemplateField
                        field={field}
                        contract={proposedContract}
                        onChange={onProposedChange}
                        validationErrors={validationErrors}
                        selectedTagIds={proposedTagIds}
                        onTagsChange={onProposedTagsChange}
                        hideToolTip
                        noMargin
                        hideAutoRenewal
                        counterPartiesSlot={proposedCounterPartiesSlot}
                    />
                </ContractAiUpdateFieldWrapper>
            </Box>
        </Box>
    );
}

export default ContractAiUpdateFieldRow;
